// import React from 'react';
// import logo from './logo.svg';
// import './App.css';

// function App() {
//   return (
//     <div className="App">
//       <header className="App-header">
//         <img src={logo} className="App-logo" alt="logo" />
//         <p>
//           Edit <code>src/App.tsx</code> and save to reload.
//         </p>
//         <a
//           className="App-link"
//           href="https://reactjs.org"
//           target="_blank"
//           rel="noopener noreferrer"
//         >
//           Learn React
//         </a>
//       </header>
//     </div>
//   );
// }

// export default App;


import { useState } from "react";
import { Navigation } from "./components/Navigation";
import { Hero } from "./components/Hero";
import { PageHero } from "./components/PageHero";
import { Home } from "./components/pages/Home";
import { About } from "./components/pages/About";
import { ProcPay } from "./components/pages/ProcPay";
import { NexAI } from "./components/pages/NexAI";
import { DemandForecasting } from "./components/pages/DemandForecasting";
import { PLM } from "./components/pages/PLM";
import { Contact } from "./components/pages/Contact";
import { Footer } from "./components/Footer";

// Step 1: Define page keys
type PageKey =
  | "home"
  | "story"
  | "mission"
  | "vision"
  | "procpay"
  | "plm"
  | "nexai"
  | "demand-forecasting"
  | "contact";

// Step 2: Define hero content type
type HeroContent = {
  title: string;
  subtitle: string;
  description: string;
  backgroundImage: string;
  breadcrumbs: { label: string }[];
  showCTAButtons?: boolean;
};

export default function App() {
  const [currentPage, setCurrentPage] = useState<PageKey>("home");

  // Step 3: Strongly typed heroContent map
  const getPageHeroContent = (page: PageKey): HeroContent | null => {
    const heroContent: Record<Exclude<PageKey, "home">, HeroContent> = {
      story: {
        title: "Born from Real Problems. Built for Real Impact.",
        subtitle: "Our Story",
        description:
          "Our story began with a simple belief — that procurement and promotional schemes in retail shouldn't be managed through spreadsheets, emails, and chaos.",
        backgroundImage:
          "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
        breadcrumbs: [{ label: "Home" }, { label: "About Us" }, { label: "Our Story" }],
      },
      mission: {
        title: "Our Mission: Simplifying Complexity. Empowering Decisions.",
        subtitle: "Our Mission",
        description:
          "We exist to transform how procurement and promotional schemes are managed—intelligently, transparently, and at scale.",
        backgroundImage:
          "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
        breadcrumbs: [{ label: "Home" }, { label: "About Us" }, { label: "Mission" }],
      },
      vision: {
        title: "Our Vision: Leading the Future of Intelligent Enterprise Operations",
        subtitle: "Our Vision",
        description:
          "We envision a world where procurement, promotions, and vendor management are seamlessly connected and autonomously optimized.",
        backgroundImage:
          "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
        breadcrumbs: [{ label: "Home" }, { label: "About Us" }, { label: "Vision" }],
      },
      procpay: {
        title: "Intelligent Procure-to-Pay Automation for the Modern Retail Enterprise",
        subtitle: "ProcPay Solutions",
        description:
          "Unify contracts, schemes, procurement, and payments with real-time control, full traceability, and AI-driven insights.",
        backgroundImage:
          "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
        breadcrumbs: [{ label: "Home" }, { label: "Solutions" }, { label: "ProcPay" }],
        showCTAButtons: true,
      },
      plm: {
        title: "AI-Powered Product Lifecycle & Supply Chain Management",
        subtitle: "ProLiMa Solutions",
        description:
          "Transform your product journey from ideation to delivery with ProLiMa by NexProcureAI — intelligent automation, predictive analytics, and real-time insights in one platform.",
        backgroundImage:
          "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
        breadcrumbs: [{ label: "Home" }, { label: "Solutions" }, { label: "ProLiMa" }],
        showCTAButtons: true,
      },
      nexai: {
        title: "Let AI Drive Smarter Procurement, Promotions & Payouts",
        subtitle: "NexAI Intelligence Engine",
        description:
          "NexAI adds intelligence, automation, and prediction to your P2P operations—helping you uncover insights, reduce risk, and boost ROI across procurement and scheme lifecycles.",
        backgroundImage:
          "https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
        breadcrumbs: [{ label: "Home" }, { label: "Solutions" }, { label: "NexAI" }],
        showCTAButtons: true,
      },
      "demand-forecasting": {
        title: "AI-Powered Demand Forecasting for Retail & Distribution",
        subtitle: "Predictive Analytics",
        description:
          "Predict demand with precision using real-time data, historical trends, and machine learning. Reduce stockouts, optimize inventory, and respond to market shifts faster than ever.",
        backgroundImage:
          "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
        breadcrumbs: [{ label: "Home" }, { label: "Solutions" }, { label: "Demand Forecasting" }],
        showCTAButtons: true,
      },
      contact: {
        title: "Let's Start a Conversation",
        subtitle: "Contact Us",
        description:
          "Whether you're curious about features, need a custom demo, or want to partner—our team is ready to connect.",
        backgroundImage:
          "https://images.unsplash.com/photo-1423666639041-f56000c27a9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
        breadcrumbs: [{ label: "Home" }, { label: "Contact" }],
      },
    };

    return page !== "home" ? heroContent[page] : null;
  };

  const renderPage = () => {
    if (["story", "mission", "vision"].includes(currentPage)) {
      return <About section={currentPage} />;
    }
    if (currentPage === "procpay") return <ProcPay />;
    if (currentPage === "nexai") return <NexAI />;
    if (currentPage === "demand-forecasting") return <DemandForecasting />;
    if (currentPage === "plm") return <PLM />;

    switch (currentPage) {
      case "contact":
        return <Contact />;
      case "home":
      default:
        return <Home />;
    }
  };

  const heroContent = getPageHeroContent(currentPage);

  return (
  <div className="min-h-screen bg-background">
    <Navigation
      currentPage={currentPage}
      onPageChange={(page: string) => setCurrentPage(page as PageKey)}
    />
    {currentPage === "home" && <Hero />}
    {currentPage !== "home" && heroContent && (
      <PageHero
        title={heroContent.title}
        subtitle={heroContent.subtitle}
        description={heroContent.description}
        backgroundImage={heroContent.backgroundImage}
        breadcrumbs={heroContent.breadcrumbs}
        showCTAButtons={heroContent.showCTAButtons}
        page={currentPage}
      />
    )}
    {renderPage()}
    <Footer onPageChange={(page: string) => setCurrentPage(page as PageKey)} />
  </div>
);

}

